import { isArrayEmpty } from 'util/isArrayEmpty';
import { createResourceContentSelector } from 'util/selector/resourceSelector';

import { createSelector } from '@reduxjs/toolkit';
import { JobModel } from 'api/dto/Job.dto';
import { JobExtendedModel } from 'api/dto/JobExtended.dto';
import { JobName } from 'model/JobName';
import { Resource } from 'model/Resource';
import { AdminState } from 'reduxStore/appStore';
import { JobStatus } from 'model/JobStatus';

import { JobState } from './initialState';

export const selectJobDomain = (state: AdminState) => state.job as JobState.Domain;

export const selectProjectJobsResource = createSelector(
  selectJobDomain,
  (domain) => domain.projectJobs.data,
);

export const selectProjectJobs = createResourceContentSelector(selectProjectJobsResource);

export const selectHasJobs = createSelector(
  selectProjectJobs,
  (jobs) => jobs && !isArrayEmpty(jobs),
);

export const selectProcessEmployeeDatabaseJobResource = createSelector(
  selectProjectJobsResource,
  (jobsResource): Resource<JobModel | null> => {
    const jobs = jobsResource.data ?? [];
    const job =
      [...jobs].sort(JobModel.sortByExecutionTime).find(JobModel.isProcessEmployeeDatabaseJob) ??
      null;
    return { data: job, loadingState: jobsResource.loadingState, error: jobsResource.error };
  },
);

export const selectProcessEmployeeDatabaseJob = createResourceContentSelector(
  selectProcessEmployeeDatabaseJobResource,
);

export const selectProcessEmployeeDatabaseIsSuccessful = createSelector(
  selectProcessEmployeeDatabaseJob,
  (job) => job && job.job.status === JobStatus.Success,
);

export const selectProcessEmployeeDatabaseJobIsVisited = createSelector(
  selectProcessEmployeeDatabaseJob,
  (job) => job && job.job.visited,
);

export const selectProcessEmployeeDatabaseJobIsNotVisited = createSelector(
  selectProcessEmployeeDatabaseJob,
  (job) => job && !job.job.visited,
);

export const selectCommitEmployeeDatabaseJobResource = createSelector(
  selectProjectJobsResource,
  (jobsResource): Resource<JobModel | null> => {
    const jobs = jobsResource.data ?? [];
    const job =
      [...jobs].sort(JobModel.sortByExecutionTime).find(JobModel.isCommitEmployeeDatabaseJob) ??
      null;
    return { data: job, loadingState: jobsResource.loadingState, error: jobsResource.error };
  },
);

export const selectCommitEmployeeDatabaseJob = createResourceContentSelector(
  selectCommitEmployeeDatabaseJobResource,
);

export const selectCommitEmployeeDatabaseJobId = createSelector(
  selectCommitEmployeeDatabaseJob,
  (job) => job && job.job.id,
);

export const selectCommitEmployeeDatabaseIsSuccessful = createSelector(
  selectCommitEmployeeDatabaseJob,
  (job) => job && job.job.status === JobStatus.Success,
);

export const selectUploadUsersJobResource = createSelector(
  selectProjectJobsResource,
  (jobsResource): Resource<JobModel | null> => {
    const jobs = jobsResource.data ?? [];
    const job =
      [...jobs].sort(JobModel.sortByExecutionTime).find(JobModel.isUploadUsersJob) ?? null;
    return { data: job, loadingState: jobsResource.loadingState, error: jobsResource.error };
  },
);

export const selectUploadUsersJob = createResourceContentSelector(selectUploadUsersJobResource);

export const selectUploadUsersJobNotVisitedReason = createSelector(selectUploadUsersJob, (job) =>
  job ? !job.job.visited && job.job.reason : null,
);

export const selectIsUploadUsersJobPending = createSelector(
  selectUploadUsersJob,
  (job) =>
    job &&
    (job.job.status === JobStatus.Execution || job.job.status === JobStatus.AwaitingReexecution),
);

export const selectIsUploadUsersJobSuccess = createSelector(
  selectUploadUsersJob,
  (job) => job && job.job.status === JobStatus.Success,
);

export const selectUploadUserJobId = createSelector(
  selectUploadUsersJob,
  (job) => job && job.job.id,
);

export const selectIsJobErrorVisited = createSelector(
  selectUploadUsersJob,
  (job) => job && job.job.visited,
);

export const selectProcessUploadEmployeeJobId = createSelector(
  selectProcessEmployeeDatabaseJob,
  (job) => {
    const extendedJob = job && job.job;

    return extendedJob && extendedJob.id;
  },
);

const selectPublishProjectSubmitsJobResource = createSelector(
  selectProjectJobsResource,
  (jobsResource): Resource<JobModel[]> => {
    const jobs = jobsResource.data ?? [];
    const filteredJobs = jobs.filter((job) => job.job.name === JobName.PublishProjectSubmits);
    return {
      data: filteredJobs,
      loadingState: jobsResource.loadingState,
      error: jobsResource.error,
    };
  },
);

export const selectPublishProjectSubmitsIsPending = createSelector(
  createResourceContentSelector(selectPublishProjectSubmitsJobResource),
  (jobs: JobModel[]) => {
    const pendingJob = jobs && jobs.find((job) => job.job.status === JobStatus.Execution);

    return pendingJob && pendingJob.job.status === JobStatus.Execution;
  },
);

export const selectLastFailPublishJob = createSelector(
  selectPublishProjectSubmitsJobResource,
  (jobResource) => {
    const jobs = jobResource.data ?? [];
    return [...jobs].sort(JobModel.sortByExecutionTime).find((job) => !!job.job.reason);
  },
);

export const selectEmailDistributionJobsResource = createSelector(
  selectJobDomain,
  (domain) => domain.emailDistributionJobs.data,
);
export const selectEmailDistributionCreateContactListJobResource = createSelector(
  selectEmailDistributionJobsResource,
  (jobsResource): Resource<JobExtendedModel | null> => {
    const jobs = jobsResource.data ?? [];
    const job = jobs.find(JobModel.isCreateContactListJob) ?? null;
    return { data: job, loadingState: jobsResource.loadingState, error: jobsResource.error };
  },
);
export const selectEmailDistributionCreateContactListJobIsSuccessful = createSelector(
  createResourceContentSelector(selectEmailDistributionCreateContactListJobResource),
  (job: JobExtendedModel | null) => job && job.status === JobStatus.Success,
);

export const selectKioskDistributionJobsResource = createSelector(
  selectJobDomain,
  (domain) => domain.kioskDistributionJobs.data,
);
export const selectKioskDistributionCreateContactListJobResource = createSelector(
  selectKioskDistributionJobsResource,
  (jobsResource): Resource<JobExtendedModel | null> => {
    const jobs = jobsResource.data ?? [];
    const job = jobs.find(JobModel.isCreateContactListJob) ?? null;
    return { data: job, loadingState: jobsResource.loadingState, error: jobsResource.error };
  },
);
export const selectKioskDistributionCreateContactListJobIsSuccessful = createSelector(
  createResourceContentSelector(selectKioskDistributionCreateContactListJobResource),
  (job: JobExtendedModel | null) => job && job.status === JobStatus.Success,
);
